import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Mail, Clock, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { postContact } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const sujets = [
  "Demande générale",
  "Inscription à une formation",
  "Demande de bourse",
  "Bénévolat",
  "Partenariat",
  "Consultation gratuite",
  "Question sur un don",
];

export default function Contact() {
  const [params] = useSearchParams();
  const preset = params.get("sujet");
  const sujetInitial =
    preset === "benevolat" ? "Bénévolat"
    : preset === "formation" ? "Inscription à une formation"
    : preset === "bourse" ? "Demande de bourse"
    : "Demande générale";

  const [form, setForm] = useState({ nom: "", courriel: "", telephone: "", sujet: sujetInitial, message: "" });
  const [envoi, setEnvoi] = useState(false);

  const update = (champ) => (e) => setForm({ ...form, [champ]: e.target.value });

  const soumettre = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      await postContact(form);
      toast.success("Message envoyé!", {
        description: "Merci de nous avoir écrit. Nous vous répondrons dans les meilleurs délais.",
      });
      setForm({ nom: "", courriel: "", telephone: "", sujet: "Demande générale", message: "" });
    } catch {
      toast.error("Une erreur est survenue", {
        description: "Votre message n'a pas pu être envoyé. Veuillez réessayer.",
      });
    } finally {
      setEnvoi(false);
    }
  };

  const inputCls =
    "w-full rounded-sm border border-input bg-background px-4 py-3.5 text-sm outline-none transition-colors duration-200 focus:border-primary focus:ring-1 focus:ring-primary";

  return (
    <>
      <section className="bg-espresso py-28 text-ivory" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Contact</p>
            <h1 className="mt-6 max-w-3xl font-serif text-5xl font-medium leading-[1.1] tracking-tight md:text-6xl" data-testid="contact-title">
              Parlons de votre <span className="italic text-primary">projet</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/70 md:text-lg">
              Inscription à une formation, demande de consultation gratuite, proposition de
              partenariat ou envie de vous impliquer : écrivez-nous, nous répondons à tous les
              messages.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-background py-20 md:py-28" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-7">
            <form onSubmit={soumettre} className="space-y-6" data-testid="contact-form">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="nom" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Nom complet *</label>
                  <input id="nom" required minLength={2} value={form.nom} onChange={update("nom")} className={inputCls} placeholder="Votre nom" data-testid="contact-nom-input" />
                </div>
                <div>
                  <label htmlFor="courriel" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Courriel *</label>
                  <input id="courriel" type="email" required value={form.courriel} onChange={update("courriel")} className={inputCls} placeholder="vous@exemple.ca" data-testid="contact-courriel-input" />
                </div>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="telephone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Téléphone</label>
                  <input id="telephone" type="tel" value={form.telephone} onChange={update("telephone")} className={inputCls} placeholder="514 000-0000" data-testid="contact-telephone-input" />
                </div>
                <div>
                  <label htmlFor="sujet" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Sujet *</label>
                  <select id="sujet" value={form.sujet} onChange={update("sujet")} className={inputCls} data-testid="contact-sujet-select">
                    {sujets.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Message *</label>
                <textarea id="message" required minLength={10} rows={7} value={form.message} onChange={update("message")} className={`${inputCls} resize-y`} placeholder="Décrivez votre demande en quelques mots…" data-testid="contact-message-textarea" />
              </div>
              <button
                type="submit"
                disabled={envoi}
                data-testid="contact-submit-button"
                className="group flex items-center gap-3 rounded-sm bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-[background-color,transform,opacity] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {envoi ? "Envoi en cours…" : "Envoyer le message"}
                <Send size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <p className="text-xs leading-relaxed text-foreground/50" data-testid="contact-consent-note">
                En envoyant ce formulaire, vous consentez à ce que vos renseignements soient
                utilisés pour répondre à votre demande, conformément à notre{" "}
                <Link to="/politique-confidentialite" className="font-semibold text-primary hover:underline" data-testid="contact-policy-link">
                  politique de protection des renseignements personnels
                </Link>
                .
              </p>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9">
            <div className="space-y-10">
              <div data-testid="contact-coordonnees">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Coordonnées</p>
                <ul className="mt-6 space-y-5 text-sm text-foreground/70">
                  <li className="flex items-start gap-3">
                    <MapPin size={18} className="mt-0.5 shrink-0 text-primary" />
                    <span>666, rue Sherbrooke Ouest, bureau 2100<br />Montréal (Québec) H3A 1E7</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Mail size={18} className="shrink-0 text-primary" />
                    <span>info@fondationhora.org</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Phone size={18} className="shrink-0 text-primary" />
                    <a href="tel:+15148977111" className="transition-colors hover:text-primary" data-testid="contact-phone">514 897-7111</a>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock size={18} className="mt-0.5 shrink-0 text-primary" />
                    <span>Réponse sous 2 jours ouvrables</span>
                  </li>
                </ul>
              </div>
              <div className="border border-border bg-card p-8" data-testid="contact-benevolat-note">
                <h2 className="font-serif text-2xl font-semibold">Envie de vous impliquer?</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground/60">
                  Juristes, fiscalistes, comptables, mentors ou simples passionnés : nos bénévoles
                  sont au cœur de la mission.{" "}
                  <Link to="/devenir-benevole" className="font-semibold text-primary hover:underline" data-testid="contact-benevole-link">
                    Rejoignez la liste des bénévoles
                  </Link>
                  .
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
